import {
  ACCESSORY,
  BASE,
  BASE_PRICE,
  CONTRACT,
  DPSUBSIDYPROMOTION,
  EXTRASIM_RATEPLAN,
  HARDWARE,
  HARDWARE_CATEGORY,
  INSTALLMENTS,
  PROMO,
  SMARTUPGRADEINSTALLMENTS,
  SMARTWATCH,
  TABLET_CATEGORY,
  TABLETS,
  WATCHES,
} from './constants'

export const getDeviceSlugPath = (productId: any, pageContent: any): string => {
  productId = productId
    ?.toLowerCase()
    ?.replace(/  +/g, ' ')
    ?.replaceAll(' ', '-');
  if (productId !== '') {
    //1st replace '+' with string plus
    //2nd replace space and & with dash and finally remove and specisl charecters except dash
    productId = productId
      ?.replace('+', ' plus')
      ?.replace(/\s+|&/, '-')
      ?.replace(/[^a-zA-Z0-9-]/g, '');
  }
  return pageContent?.deviceDetailLink + productId;
};

export const alterHardwarePriceFormat = (hardwarePrices: any[]): void => {
  const alteredPrices: any = {};
  (hardwarePrices || [])?.forEach((data: any) => {
    let priceContractTypeKey: string = data?.priceCategory;

    if (
      data?.duration === 0 &&
      !data?.residual &&
      data?.priceCategory === BASE
    ) {
      priceContractTypeKey = BASE_PRICE;
    } else if (
      data?.duration === 24 &&
      !data?.residual &&
      data?.priceCategory === CONTRACT
    ) {
      priceContractTypeKey = INSTALLMENTS;
    } else if (
      data?.duration === 24 &&
      data?.residual &&
      data?.priceCategory === CONTRACT
    ) {
      priceContractTypeKey = SMARTUPGRADEINSTALLMENTS;
    } else if (
      data?.duration === 0 &&
      !data?.residual &&
      data?.priceCategory === PROMO
    ) {
      priceContractTypeKey = DPSUBSIDYPROMOTION;
    }
    alteredPrices[priceContractTypeKey] = data;
  });
  return alteredPrices;
};

  /**
   * Helper to extract the hardwareCategory from a cart item's notes.
   */
  const getHardwareCategoryFromNotes = (item: any): string | undefined =>
    item?.note?.find((n: any) => n?.author === HARDWARE_CATEGORY)?.text;

  /** Maps hardwareCategory note values to our category keys */
  const HARDWARE_CATEGORY_MAP: Record<string, string> = {
    [SMARTWATCH]: WATCHES,
    [TABLET_CATEGORY]: TABLETS,
  };

  /**
   * Scans the cart and returns all items grouped by category in a single pass.
   * Supports both the simplified cart structure (flat cartItem[].items[]) and the
   * detailed cart structure (nested cartItem[].cartItem[]... with productOffering.poType).
   *
   * Returned shape:
   * {
   *   ACCESSORY: { [itemId]: item[] },
   *   WATCHES:   { [itemId]: item[] },
   *   TABLETS:   { [itemId]: item[] },
   * }
   *
   * @param cartItems - The top-level cartItem array from either structure
   */
  export const findAllCartItemsByCategory = (
    cartItems?: any[]
  ): Record<string, any> => {
    const result: Record<string, any> = {
      [ACCESSORY]: {},
      [WATCHES]: {},
      [TABLETS]: {},
      ratePlanInfo: {},  // { WATCHES: { name, monthlyPrice }, TABLETS: { name, monthlyPrice } }
    };

    if (!cartItems?.length) return result;

    const addItem = (category: string, id: string, item: any) => {
      if (!id) return;
      if (!result[category][id]) result[category][id] = [];
      result[category][id].push(item);
    };

    // Add processType globally from any cartItem entry (common for all tabs)
    for (const entry of cartItems) {
      if (entry?.processType?.id && !result.processType) {
        result.processType = entry.processType.id;
        break;
      }
    }

    // Recursively walk nested cartItem[] tree to find subscribedInfo with actual data
    // (DEVICE_RENEWAL: subscribedInfo is on a deeply nested leaf node, not the root)
    const extractSubscribedInfo = (items: any[]): void => {
      for (const entry of items) {
        // A valid subscribedInfo has at least one non-empty key (e.g. id or msisdn)
        if (entry?.subscribedInfo && Object.keys(entry.subscribedInfo).length > 0) {
          result.subscribedInfo = entry.subscribedInfo;
          return; // stop as soon as we find the first real one
        }
        if (entry?.cartItem?.length) {
          extractSubscribedInfo(entry.cartItem);
          if (result.subscribedInfo) return; // stop recursing once found
        }
      }
    };
    extractSubscribedInfo(cartItems);

    // --- Simplified structure: cartItem[].items[] with itemType ---
    const searchFlatCartItems = (items: any[]) => {
      // Heuristic classifier for flat EXTRA_SIM rate-plan labels.
      // Uses lexical hints from rate-plan name/id to map to WATCHES/TABLETS.
      const resolveHardwareCategoryFromRatePlanLabel = (ratePlanLabel: string): string | undefined => {
        const normalizedLabel = (ratePlanLabel || '').toLowerCase();
        if (!normalizedLabel) return undefined;
        if (normalizedLabel.includes('watch')) return WATCHES;
        if (normalizedLabel.includes('tablet') || normalizedLabel.includes('surf')) return TABLETS;
        return undefined;
      };

      for (const entry of items) {
        if (entry?.items?.length) {
          const entryItems = entry.items;
          const hardwareCategoriesInEntry = new Set<string>();

          for (const item of entry.items) {
            if (item?.itemType === ACCESSORY) {
              addItem(ACCESSORY, item?.id, item);
            } else if (item?.itemType === HARDWARE) {
              const hwCategory = HARDWARE_CATEGORY_MAP[getHardwareCategoryFromNotes(item) || ''];
              if (hwCategory) {
                addItem(hwCategory, item?.id, item);
                hardwareCategoriesInEntry.add(hwCategory);
              }
            }
          }

          // Flat EXTRA_SIM payload keeps rate-plans as sibling items in the same entry.
          // Resolution order:
          // 1) label-based category inference,
          // 2) positional alignment with discovered hardware categories,
          // 3) first still-unassigned hardware category.
          const extraSimRatePlanItems = entryItems.filter((item: any) => item?.itemType === EXTRASIM_RATEPLAN);
          const hardwareCategoryArray = Array.from(hardwareCategoriesInEntry);
          const unassignedCategories = hardwareCategoryArray.filter((cat) => !result.ratePlanInfo[cat]);

          extraSimRatePlanItems.forEach((ratePlanItem: any, index: number) => {
            const inferredCategory = resolveHardwareCategoryFromRatePlanLabel(ratePlanItem?.name || ratePlanItem?.id || '');
            const positionalCategory = hardwareCategoryArray[index];
            const fallbackUnassignedCategory = unassignedCategories.find((cat) => !result.ratePlanInfo[cat]);
            const hwCategory = inferredCategory || positionalCategory || fallbackUnassignedCategory;
            if (hwCategory && !result.ratePlanInfo[hwCategory]) {
              const recurringPrice = ratePlanItem?.prices?.find((price: any) => price?.priceType === 'RECURRING')?.value;
              result.ratePlanInfo[hwCategory] = {
                name: ratePlanItem?.name || ratePlanItem?.id || '',
                monthlyPrice: +(recurringPrice || 0),
              };
            }
          });
        }
      }
    };

    /**
     * Extract rateplan info from sibling items under an EXTRASIM_RATEPLAN parent.
     * The rateplan child is the one that is NOT a HARDWARE/SIMCARD bundle.
     */
    const extractRatePlanFromParent = (parentItem: any, hwCategory: string) => {
      const siblings = parentItem?.cartItem || [];
      for (const sibling of siblings) {
        const siblingPoType = sibling?.productOffering?.poType;
        // Skip HARDWARE / SIMCARD bundles — we want the rateplan leaf
        if (siblingPoType === HARDWARE || siblingPoType === 'SIMCARD') continue;
        // The rateplan leaf has no poType and has monthly pricing
        const rpItem = sibling;
        const prices = rpItem?.itemPrice?.[0]?.cartPrice || [];
        const monthlyEntry = prices.find((p: any) => p?.recurringChargePeriod === 'monthly');
        if (monthlyEntry) {
          result.ratePlanInfo[hwCategory] = {
            name: rpItem?.productOffering?.name || rpItem?.productOffering?.id || '',
            monthlyPrice: +(monthlyEntry?.price?.taxIncludedAmount?.value || 0),
          };
          break;
        }
      }
    };

    // --- Detailed structure: nested cartItem[] with productOffering.poType ---
    const searchNestedCartItems = (items: any[], parentItem?: any) => {
      for (const item of items) {
        const poType = item?.productOffering?.poType;

        if (poType === ACCESSORY) {
          const children = item?.cartItem?.length ? item.cartItem : [item];
          children.forEach((child: any) =>
            addItem(ACCESSORY, child?.productOffering?.id || child?.id, child)
          );
        } else if (poType === HARDWARE) {
          const children = item?.cartItem?.length ? item.cartItem : [item];
          children.forEach((child: any) => {
            const hwCategory = HARDWARE_CATEGORY_MAP[getHardwareCategoryFromNotes(child) || ''];
            if (hwCategory) {
              addItem(hwCategory, child?.productOffering?.id || child?.id, child);
              // Extract rateplan info from the parent (EXTRASIM_RATEPLAN) if available
              if (parentItem && !result.ratePlanInfo[hwCategory]) {
                extractRatePlanFromParent(parentItem, hwCategory);
              }
            }
          });
        }

        // Recurse into nested cartItem arrays
        if (item?.cartItem?.length) {
          searchNestedCartItems(item.cartItem, item);
        }
      }
    };

    searchFlatCartItems(cartItems);
    searchNestedCartItems(cartItems);

    return result;
  };
